import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Widgets as TangibleIcon,
  Stars as IntangibleIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';
import { fixedAssetsService } from '../../services/fixedAssets.service';
import {
  AssetType,
  CreateFixedAssetCategoryDto,
  DepreciationMethod,
  FixedAssetCategory,
} from '../../types/fixedAssets.types';

// ─── Translations ─────────────────────────────────────────────────────────────

const translations = {
  en: {
    title: 'Fixed Asset Categories',
    addCategory: 'Add Category',
    refresh: 'Refresh',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading...',
    noData: 'No categories found',
    confirmDelete: 'Are you sure you want to delete this category? This action cannot be undone.',
    // Stats
    totalCategories: 'Total Categories',
    tangibleCount: 'Tangible',
    intangibleCount: 'Intangible',
    // Table headers
    nameEn: 'Name (EN)',
    nameAr: 'Name (AR)',
    assetType: 'Asset Type',
    defaultUsefulLife: 'Useful Life (yrs)',
    defaultMethod: 'Method',
    status: 'Status',
    actions: 'Actions',
    // Asset types
    tangible: 'Tangible',
    intangible: 'Intangible',
    // Methods
    straightLine: 'Straight Line',
    decliningBalance: 'Declining Balance',
    // Status
    active: 'Active',
    inactive: 'Inactive',
    // Dialog
    addTitle: 'Add Asset Category',
    editTitle: 'Edit Asset Category',
    categoryName: 'Category Name (EN)',
    categoryNameAr: 'Category Name (AR)',
    assetTypeLabel: 'Asset Type',
    usefulLifeLabel: 'Default Useful Life (years)',
    methodLabel: 'Default Depreciation Method',
    decliningRateLabel: 'Default Declining Rate (%)',
    isActiveLabel: 'Active',
    // Errors
    nameRequired: 'Category name is required',
    nameArRequired: 'Arabic category name is required',
    // Toast
    savedSuccess: 'Category saved successfully',
    deletedSuccess: 'Category deleted successfully',
    saveFailed: 'Failed to save category',
    deleteFailed: 'Failed to delete category',
  },
  ar: {
    title: 'فئات الأصول الثابتة',
    addCategory: 'إضافة فئة',
    refresh: 'تحديث',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    loading: 'جاري التحميل...',
    noData: 'لا توجد فئات',
    confirmDelete: 'هل أنت متأكد من حذف هذه الفئة؟ لا يمكن التراجع عن هذا الإجراء.',
    // Stats
    totalCategories: 'إجمالي الفئات',
    tangibleCount: 'ملموسة',
    intangibleCount: 'غير ملموسة',
    // Table headers
    nameEn: 'الاسم (EN)',
    nameAr: 'الاسم (AR)',
    assetType: 'نوع الأصل',
    defaultUsefulLife: 'العمر الافتراضي (سنة)',
    defaultMethod: 'طريقة الاستهلاك',
    status: 'الحالة',
    actions: 'الإجراءات',
    // Asset types
    tangible: 'ملموسة',
    intangible: 'غير ملموسة',
    // Methods
    straightLine: 'القسط الثابت',
    decliningBalance: 'القسط المتناقص',
    // Status
    active: 'نشط',
    inactive: 'غير نشط',
    // Dialog
    addTitle: 'إضافة فئة أصول',
    editTitle: 'تعديل فئة أصول',
    categoryName: 'اسم الفئة (EN)',
    categoryNameAr: 'اسم الفئة (AR)',
    assetTypeLabel: 'نوع الأصل',
    usefulLifeLabel: 'العمر الإنتاجي الافتراضي (سنة)',
    methodLabel: 'طريقة الاستهلاك الافتراضية',
    decliningRateLabel: 'نسبة التناقص الافتراضية (%)',
    isActiveLabel: 'نشط',
    // Errors
    nameRequired: 'اسم الفئة مطلوب',
    nameArRequired: 'الاسم العربي للفئة مطلوب',
    // Toast
    savedSuccess: 'تم حفظ الفئة بنجاح',
    deletedSuccess: 'تم حذف الفئة بنجاح',
    saveFailed: 'فشل حفظ الفئة',
    deleteFailed: 'فشل حذف الفئة',
  },
};

// ─── Default form state ───────────────────────────────────────────────────────

const defaultForm: CreateFixedAssetCategoryDto & { isActive: boolean } = {
  categoryName: '',
  categoryNameAr: '',
  assetType: 'Tangible',
  defaultUsefulLifeYears: undefined,
  defaultDepreciationMethod: 'StraightLine',
  defaultDecliningRate: undefined,
  isActive: true,
};

// ─── Component ────────────────────────────────────────────────────────────────

const FixedAssetCategoriesPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [categories, setCategories] = useState<FixedAssetCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FixedAssetCategory | null>(null);
  const [form, setForm] = useState<CreateFixedAssetCategoryDto & { isActive: boolean }>(defaultForm);
  const [errors, setErrors] = useState<{ categoryName?: string; categoryNameAr?: string }>({});
  const [saving, setSaving] = useState(false);

  // ── Stats ──────────────────────────────────────────────────────────────────

  const totalCount = categories.length;
  const tangibleCount = categories.filter((c) => c.assetType === 'Tangible').length;
  const intangibleCount = categories.filter((c) => c.assetType === 'Intangible').length;

  // ── Data loading ───────────────────────────────────────────────────────────

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await fixedAssetsService.getAllCategories();
      setCategories(data);
    } catch {
      toast.error(isRTL ? 'فشل تحميل الفئات' : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // ── Dialog helpers ─────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditing(null);
    setForm(defaultForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (category: FixedAssetCategory) => {
    setEditing(category);
    setForm({
      categoryName: category.categoryName,
      categoryNameAr: category.categoryNameAr,
      assetType: category.assetType,
      defaultUsefulLifeYears: category.defaultUsefulLifeYears,
      defaultDepreciationMethod: category.defaultDepreciationMethod ?? 'StraightLine',
      defaultDecliningRate: category.defaultDecliningRate,
      isActive: category.isActive,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  // ── Form field handlers ────────────────────────────────────────────────────

  const handleTextField = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNumberField = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === '' ? undefined : Number(e.target.value);
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleSelectAssetType = (e: SelectChangeEvent<AssetType>) => {
    setForm((prev) => ({ ...prev, assetType: e.target.value as AssetType }));
  };

  const handleSelectMethod = (e: SelectChangeEvent<DepreciationMethod>) => {
    const method = e.target.value as DepreciationMethod;
    setForm((prev) => ({
      ...prev,
      defaultDepreciationMethod: method,
      // Clear declining rate when switching away from DecliningBalance
      defaultDecliningRate: method === 'DecliningBalance' ? prev.defaultDecliningRate : undefined,
    }));
  };

  const handleActiveSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!form.categoryName.trim()) newErrors.categoryName = t.nameRequired;
    if (!form.categoryNameAr.trim()) newErrors.categoryNameAr = t.nameArRequired;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const dto: CreateFixedAssetCategoryDto & { isActive?: boolean } = {
        categoryName: form.categoryName.trim(),
        categoryNameAr: form.categoryNameAr.trim(),
        assetType: form.assetType,
        defaultUsefulLifeYears: form.defaultUsefulLifeYears,
        defaultDepreciationMethod: form.defaultDepreciationMethod,
        defaultDecliningRate:
          form.defaultDepreciationMethod === 'DecliningBalance' ? form.defaultDecliningRate : undefined,
        isActive: form.isActive,
      };
      if (editing) {
        await fixedAssetsService.updateCategory(editing.categoryId, dto as any);
      } else {
        await fixedAssetsService.createCategory(dto);
      }
      toast.success(t.savedSuccess);
      closeDialog();
      loadCategories();
    } catch {
      toast.error(t.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (category: FixedAssetCategory) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      await fixedAssetsService.deleteCategory(category.categoryId);
      toast.success(t.deletedSuccess);
      loadCategories();
    } catch {
      toast.error(t.deleteFailed);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const assetTypeChip = (type: AssetType) => (
    <Chip
      icon={type === 'Tangible' ? <TangibleIcon /> : <IntangibleIcon />}
      label={type === 'Tangible' ? t.tangible : t.intangible}
      size="small"
      sx={{
        fontWeight: 'bold',
        bgcolor: type === 'Tangible' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(156, 39, 176, 0.12)',
        color: type === 'Tangible' ? '#1565c0' : '#7b1fa2',
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );

  const methodLabel = (method?: DepreciationMethod) => {
    if (!method) return '-';
    return method === 'StraightLine' ? t.straightLine : t.decliningBalance;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── Stat Cards ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Total */}
        <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: 200 }}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{totalCount}</Typography>
                  <Typography variant="body2">{t.totalCategories}</Typography>
                </Box>
                <CategoryIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Tangible */}
        <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: 200 }}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{tangibleCount}</Typography>
                  <Typography variant="body2">{t.tangibleCount}</Typography>
                </Box>
                <TangibleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Intangible */}
        <Box sx={{ flex: '1 1 calc(33.33% - 12px)', minWidth: 200 }}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #9c27b0 0%, #ce93d8 100%)',
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{intangibleCount}</Typography>
                  <Typography variant="body2">{t.intangibleCount}</Typography>
                </Box>
                <IntangibleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ── Header ── */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CategoryIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadCategories}
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' },
              }}
            >
              {t.addCategory}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* ── Table ── */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.nameEn}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.nameAr}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.assetType}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.defaultUsefulLife}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.defaultMethod}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((cat) => (
                  <TableRow
                    key={cat.categoryId}
                    hover
                    sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{cat.categoryName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="medium" dir="rtl">{cat.categoryNameAr}</Typography>
                    </TableCell>
                    <TableCell>{assetTypeChip(cat.assetType)}</TableCell>
                    <TableCell align="center">
                      <Typography>
                        {cat.defaultUsefulLifeYears != null ? cat.defaultUsefulLifeYears : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{methodLabel(cat.defaultDepreciationMethod)}</Typography>
                      {cat.defaultDepreciationMethod === 'DecliningBalance' && cat.defaultDecliningRate != null && (
                        <Typography variant="caption" color="text.secondary">
                          {cat.defaultDecliningRate}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={cat.isActive ? <CheckCircleIcon /> : undefined}
                        label={cat.isActive ? t.active : t.inactive}
                        color={cat.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t.edit}>
                          <IconButton
                            size="small"
                            onClick={() => openEdit(cat)}
                            sx={{
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.delete}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(cat)}
                            sx={{
                              color: 'error.main',
                              '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* ── Add / Edit Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        dir={isRTL ? 'rtl' : 'ltr'}
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2,
          }}
        >
          <CategoryIcon />
          <Typography variant="h6" fontWeight={700} component="span">
            {editing ? t.editTitle : t.addTitle}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>
          {/* Category Name EN */}
          <TextField
            label={t.categoryName}
            value={form.categoryName}
            onChange={handleTextField('categoryName')}
            error={!!errors.categoryName}
            helperText={errors.categoryName}
            fullWidth
            size="small"
          />

          {/* Category Name AR */}
          <TextField
            label={t.categoryNameAr}
            value={form.categoryNameAr}
            onChange={handleTextField('categoryNameAr')}
            error={!!errors.categoryNameAr}
            helperText={errors.categoryNameAr}
            fullWidth
            size="small"
            inputProps={{ dir: 'rtl' }}
          />

          {/* Asset Type */}
          <FormControl fullWidth size="small">
            <InputLabel>{t.assetTypeLabel}</InputLabel>
            <Select<AssetType>
              value={form.assetType}
              label={t.assetTypeLabel}
              onChange={handleSelectAssetType}
            >
              <MenuItem value="Tangible">{t.tangible}</MenuItem>
              <MenuItem value="Intangible">{t.intangible}</MenuItem>
            </Select>
          </FormControl>

          {/* Default Useful Life */}
          <TextField
            label={t.usefulLifeLabel}
            type="number"
            value={form.defaultUsefulLifeYears ?? ''}
            onChange={handleNumberField('defaultUsefulLifeYears')}
            fullWidth
            size="small"
            inputProps={{ min: 1 }}
          />

          {/* Default Method */}
          <FormControl fullWidth size="small">
            <InputLabel>{t.methodLabel}</InputLabel>
            <Select<DepreciationMethod>
              value={form.defaultDepreciationMethod ?? 'StraightLine'}
              label={t.methodLabel}
              onChange={handleSelectMethod}
            >
              <MenuItem value="StraightLine">{t.straightLine}</MenuItem>
              <MenuItem value="DecliningBalance">{t.decliningBalance}</MenuItem>
            </Select>
          </FormControl>

          {/* Declining Rate — only shown when DecliningBalance */}
          {form.defaultDepreciationMethod === 'DecliningBalance' && (
            <TextField
              label={t.decliningRateLabel}
              type="number"
              value={form.defaultDecliningRate ?? ''}
              onChange={handleNumberField('defaultDecliningRate')}
              fullWidth
              size="small"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
          )}

          {/* Active Switch */}
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={handleActiveSwitch}
                color="success"
              />
            }
            label={t.isActiveLabel}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={closeDialog} disabled={saving}>
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssetCategoriesPage;
